
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";
import { RichTextEditor } from "@/components/ui/rich-text-editor";

type WorldCategory = Tables<"world_categories">;
type WorldItem = Tables<"world_items">;

interface WorldItemFormProps {
  item?: WorldItem | null;
  categories: WorldCategory[];
  onClose: () => void;
  onSuccess: () => void;
}

const WorldItemForm = ({ item, categories, onClose, onSuccess }: WorldItemFormProps) => {
  const [formData, setFormData] = useState({
    category_id: "",
    name: "",
    image_url: "",
    description: "",
    fun_fact: "",
    habitat: "",
    sound: "",
    color: "",
    season: "",
    type: "",
    display_order: 0,
    seo_title: "",
    meta_description: "",
    slug: "",
    keywords: "",
    image_alt_text: "",
    video_url: ""
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const selectedCategory = categories.find((c) => String(c.id) === formData.category_id);

  useEffect(() => {
    console.log("WorldItemForm - useEffect triggered");
    console.log("WorldItemForm - item prop:", item);
    console.log("WorldItemForm - item type:", typeof item);
    console.log("WorldItemForm - item keys:", item ? Object.keys(item) : "null");
    
    if (item && item.id) {
      console.log("Setting form data with item:", item);
      console.log("Item name:", item.name);
      console.log("Item category_id:", item.category_id);
      
      setFormData({
        category_id: String(item.category_id || ""),
        name: String(item.name || ""),
        image_url: String(item.image_url || ""),
        description: String(item.description || ""),
        fun_fact: String(item.fun_fact || ""),
        habitat: String(item.habitat || ""),
        sound: String(item.sound || ""),
        color: String(item.color || ""),
        season: String(item.season || ""),
        type: String(item.type || ""),
        display_order: Number(item.display_order || 0),
        seo_title: String(item.seo_title || ""),
        meta_description: String(item.meta_description || ""),
        slug: String(item.slug || ""),
        keywords: Array.isArray(item.keywords) ? item.keywords.join(", ") : "",
        image_alt_text: String(item.image_alt_text || ""),
        video_url: String((item as any).video_url || "")
      });
      
      console.log("Form data set successfully");
    } else {
      console.log("No valid item provided, resetting form");
      // Reset form when no item is provided (for creating new items)
      setFormData({
        category_id: "",
        name: "",
        image_url: "",
        description: "",
        fun_fact: "",
        habitat: "",
        sound: "",
        color: "",
        season: "",
        type: "",
        display_order: 0,
        seo_title: "",
        meta_description: "",
        slug: "",
        keywords: "",
        image_alt_text: "",
        video_url: ""
      });
    }
  }, [item]);

  // Ensure full, fresh item data is loaded when editing (covers cases where list payload is partial)
  useEffect(() => {
    const loadItemFromDb = async () => {
      if (!item?.id) return;
      try {
        const { data, error } = await supabase
          .from("world_items")
          .select("*")
          .eq("id", item.id)
          .maybeSingle();

        if (error) {
          console.error("Failed to fetch item details:", error);
          return;
        }

        if (data) {
          setFormData({
            category_id: String(data.category_id || ""),
            name: String(data.name || ""),
            image_url: String(data.image_url || ""),
            description: String(data.description || ""),
            fun_fact: String(data.fun_fact || ""),
            habitat: String(data.habitat || ""),
            sound: String(data.sound || ""),
            color: String(data.color || ""),
            season: String(data.season || ""),
            type: String(data.type || ""),
            display_order: Number(data.display_order || 0),
            seo_title: String(data.seo_title || ""),
            meta_description: String(data.meta_description || ""),
            slug: String(data.slug || ""),
            keywords: Array.isArray(data.keywords) ? data.keywords.join(", ") : "",
            image_alt_text: String(data.image_alt_text || ""),
            video_url: String((data as any).video_url || "")
          });
        }
      } catch (err) {
        console.error("Unexpected error loading item details:", err);
      }
    };

    loadItemFromDb();
  }, [item?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Auto-generate slug if empty
      const autoSlug = formData.slug || formData.name.toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

      const keywordsArray = formData.keywords
        .split(",")
        .filter(keyword => keyword.trim())
        .map(keyword => keyword.trim());

      const submitData = {
        ...formData,
        category_id: formData.category_id || null,
        habitat: formData.habitat || null,
        sound: formData.sound || null,
        color: formData.color || null,
        season: formData.season || null,
        type: formData.type || null,
        slug: autoSlug,
        keywords: keywordsArray.length > 0 ? keywordsArray : null,
        seo_title: formData.seo_title || null,
        meta_description: formData.meta_description || null,
        image_alt_text: formData.image_alt_text || null,
        video_url: formData.video_url || null
      };

      if (item) {
        // Update existing item
        const { error } = await supabase
          .from("world_items")
          .update(submitData)
          .eq("id", item.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Item updated successfully"
        });
      } else {
        // Create new item
        const { error } = await supabase
          .from("world_items")
          .insert(submitData);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Item created successfully"
        });
      }

      onSuccess();
    } catch (error) {
      console.error("Error saving item:", error);
      toast({
        title: "Error",
        description: "Failed to save item",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent key={item?.id || 'new'} className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {item ? "Edit Item" : "Add New Item"}
          </DialogTitle>
        </DialogHeader>
        
        <form key={item?.id || 'new'} onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="category_id">Category</Label>
              <Select
                value={formData.category_id}
                onValueChange={(value) => setFormData((prev) => ({ ...prev, category_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {!selectedCategory && formData.category_id && (
                    <SelectItem value={formData.category_id}>Current (inactive)</SelectItem>
                  )}
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={String(category.id)}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="name">Item Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="image_url">Image URL (thumbnail / fallback)</Label>
            <Input
              id="image_url"
              value={formData.image_url}
              onChange={(e) => setFormData((prev) => ({ ...prev, image_url: e.target.value }))}
              placeholder="https://example.com/image.jpg"
              required
            />
          </div>

          {/* 🎥 Video URL — highlighted block so admins always notice it */}
          <div className="rounded-xl border-2 border-primary/40 bg-primary/5 p-4 space-y-2">
            <Label htmlFor="video_url" className="text-base font-bold flex items-center gap-2">
              🎥 Video URL <span className="text-xs font-normal text-muted-foreground">(9:16 vertical — YouTube Shorts or .mp4)</span>
            </Label>
            <Input
              id="video_url"
              value={formData.video_url}
              onChange={(e) => setFormData((prev) => ({ ...prev, video_url: e.target.value }))}
              placeholder="https://youtube.com/shorts/XXXX or https://example.com/video.mp4"
              className="bg-background"
            />
            <p className="text-xs text-muted-foreground">
              💡 Paste a YouTube Shorts link or a direct <code>.mp4</code> URL. Leave empty to show the image instead.
            </p>
            {formData.video_url && (
              <p className="text-xs text-green-600 font-medium">✅ Video will be shown on the kids' page</p>
            )}
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <RichTextEditor
              value={formData.description}
              onChange={(value) => setFormData((prev) => ({ ...prev, description: value }))}
              placeholder="Enter detailed description of the world item"
              height="150px"
            />
          </div>

          <div>
            <Label htmlFor="fun_fact">Fun Fact</Label>
            <Textarea
              id="fun_fact"
              value={formData.fun_fact}
              onChange={(e) => setFormData((prev) => ({ ...prev, fun_fact: e.target.value }))}
              rows={2}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="habitat">Habitat (Optional)</Label>
              <Input
                id="habitat"
                value={formData.habitat}
                onChange={(e) => setFormData((prev) => ({ ...prev, habitat: e.target.value }))}
              />
            </div>

            <div>
              <Label htmlFor="sound">Sound (Optional)</Label>
              <Input
                id="sound"
                value={formData.sound}
                onChange={(e) => setFormData((prev) => ({ ...prev, sound: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="color">Color (Optional)</Label>
              <Input
                id="color"
                value={formData.color}
                onChange={(e) => setFormData((prev) => ({ ...prev, color: e.target.value }))}
              />
            </div>

            <div>
              <Label htmlFor="season">Season (Optional)</Label>
              <Input
                id="season"
                value={formData.season}
                onChange={(e) => setFormData((prev) => ({ ...prev, season: e.target.value }))}
              />
            </div>

            <div>
              <Label htmlFor="type">Type (Optional)</Label>
              <Input
                id="type"
                value={formData.type}
                onChange={(e) => setFormData((prev) => ({ ...prev, type: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="display_order">Display Order</Label>
            <Input
              id="display_order"
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData((prev) => ({ ...prev, display_order: Number.isNaN(parseInt(e.target.value)) ? 0 : parseInt(e.target.value) }))}
              min="0"
            />
          </div>

          {/* SEO Fields Section */}
          <div className="border-t pt-4 space-y-4">
            <h3 className="text-lg font-semibold text-muted-foreground">SEO Settings (Optional)</h3>
            
            <div>
              <Label htmlFor="seo_title">SEO Title</Label>
              <Input
                id="seo_title"
                value={formData.seo_title}
                onChange={(e) => setFormData((prev) => ({ ...prev, seo_title: e.target.value }))}
                placeholder="Custom SEO title for search engines"
                maxLength={60}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData.seo_title.length}/60 characters
              </p>
            </div>

            <div>
              <Label htmlFor="meta_description">Meta Description</Label>
              <Textarea
                id="meta_description"
                value={formData.meta_description}
                onChange={(e) => setFormData((prev) => ({ ...prev, meta_description: e.target.value }))}
                placeholder="Brief description for search engines"
                rows={2}
                maxLength={160}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData.meta_description.length}/160 characters
              </p>
            </div>

            <div>
              <Label htmlFor="slug">URL Slug</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => setFormData((prev) => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
                placeholder="Auto-generated from name if empty"
              />
              <p className="text-xs text-muted-foreground mt-1">
                URL: /world/{formData.slug || formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}
              </p>
            </div>

            <div>
              <Label htmlFor="keywords">Keywords / Tags</Label>
              <Input
                id="keywords"
                value={formData.keywords}
                onChange={(e) => setFormData((prev) => ({ ...prev, keywords: e.target.value }))}
                placeholder="animals, nature, wildlife (comma-separated)"
              />
            </div>

            <div>
              <Label htmlFor="image_alt_text">Image Alt Text</Label>
              <Input
                id="image_alt_text"
                value={formData.image_alt_text}
                onChange={(e) => setFormData((prev) => ({ ...prev, image_alt_text: e.target.value }))}
                placeholder="Descriptive text for the image"
              />
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : item ? "Update" : "Create"}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default WorldItemForm;
